import { Component, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  private readonly SCRIPT_TAG = 'script';
  private BLANK = '';
  displayComponent = true;
  dynaTraceScript!: HTMLScriptElement;

  constructor(private readonly router: Router) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.displayComponent = !(event.url.includes('/report') || event.url.includes('/create-form'));
      }
    });
  }

  ngOnInit(): void {
    this.loadDynatraceScript();
    this.loadWalkmeLibrary();
  }

  /**
   * Dynamically loads the WalkMe script into the application.
   * This script is used for integrating WalkMe's guidance and engagement tools.
   *
   * Steps:
   * 1. Checks if the `walkmeUrl` is defined in the environment configuration.
   * 2. Ensures the `document.body` is available before proceeding.
   * 3. Creates a `<script>` element dynamically and sets its attributes:
   *    - `innerHTML`: Empty string.
   *    - `async`: Enables asynchronous loading.
   *    - `crossOrigin`: Sets the cross-origin attribute to `anonymous`.
   *    - `src`: Sets the script source to the `walkmeUrl`.
   * 4. Appends the script to the `<body>` of the document.
   * 5. Logs success or handles errors gracefully using a `try-catch` block.
   *
   * @private
   * @returns {void}
   */

  private loadWalkmeLibrary() {
    if (!environment.walkmeUrl) {
      console.warn('WalkMe URL is not defined in the environment configuration.');
      return;
    }

    try {
      const documentBody = document.body;
      if (!documentBody) {
        console.error('Document body is not available.');
        return;
      }

      const walkmeScriptElement = document.createElement(this.SCRIPT_TAG);
      walkmeScriptElement.innerHTML = '';
      walkmeScriptElement.async = true;
      walkmeScriptElement.crossOrigin = 'anonymous';
      walkmeScriptElement.src = environment.walkmeUrl;

      documentBody.appendChild(walkmeScriptElement);
      console.log('WalkMe script successfully added to the document.');
    } catch (error) {
      console.error('Failed to load WalkMe library:', error);
    }
  }

  /**
   * Dynamically loads the Dynatrace script into the application's <head> element.
   * This script is used for performance monitoring in staging and production environments.
   *
   * Checks if the dynaTraceUrl is defined in the environment configuration.
   * If defined, creates a <script> element, sets its attributes, and appends it to <head>.
   *
   * @private
   * @returns {void}
   */
  private loadDynatraceScript(): void {
    if (environment.dynaTraceUrl) {
      console.log('Dynatrace URL found, injecting script:', environment.dynaTraceUrl);
      this.dynaTraceScript = document.createElement('script');
      this.dynaTraceScript.type = 'text/javascript';
      this.dynaTraceScript.src = environment.dynaTraceUrl;
      this.dynaTraceScript.crossOrigin = 'anonymous';
      document.head.appendChild(this.dynaTraceScript);
      console.log('Dynatrace script successfully appended to <head>.');
    } else {
      console.warn('Dynatrace URL not defined in environment configuration. Script not injected.');
    }
  }
}
